// Raw desktop (keyboard + mouse) input state. The VR side drives everything
// through WebXR controllers; on desktop we synthesize a single pointer from
// the mouse. This module only tracks RAW input — main.ts turns it into a
// world ray, drives a synthetic "controller 0", and runs the shared pick +
// gesture pipeline. Kept dependency-free so it's easy to read/extend.

import * as THREE from "three";

import { elementRect } from "./element-rect";

export const desktopMouse = {
  // Normalized device coords of the cursor over the canvas (−1..1, y up).
  ndc: new THREE.Vector2(0, 0),
  // Raw client (CSS px) coords — used for screen-space UI like the corner
  // navigation gizmo whose viewport is in fixed pixels, not camera NDC.
  clientX: 0,
  clientY: 0,
  // True while the cursor is over the canvas (pointer hasn't left).
  overCanvas: false,
  // True while a right-button (orbit) or middle-button (pan) drag is moving the
  // camera. Hover highlighting checks this so nothing lights up under the cursor
  // while you swing the view around — a right/middle drag is ALWAYS a camera
  // move (LMB drives the tools), so this never hides a tool's own hover.
  camDragging: false,
  // Left mouse button held (the "trigger" equivalent).
  lmb: false,
  // Modifier keys captured at the moment of the last LMB press — used by the
  // marquee to add (shift) or subtract (ctrl/cmd) instead of replacing, or
  // (alt) to select only the front-most layer instead of through the depth.
  pressShift: false,
  pressSubtract: false,
  pressAlt: false,
};

// One-shot edge flags: set by the listeners, consumed once per frame by the
// render loop so a press/release maps to a single selectstart/selectend.
let _pressed = false;
let _released = false;

export function consumeDesktopPress(): boolean {
  const v = _pressed;
  _pressed = false;
  return v;
}
export function consumeDesktopRelease(): boolean {
  const v = _released;
  _released = false;
  return v;
}

// Attach listeners to the renderer's canvas. LMB is the tool button; orbit /
// pan / zoom live on the other mouse buttons (configured on OrbitControls).
export function installDesktopMouse(dom: HTMLElement): void {
  const updateNDC = (e: PointerEvent) => {
    const r = elementRect(dom);
    desktopMouse.ndc.set(
      ((e.clientX - r.left) / r.width) * 2 - 1,
      -((e.clientY - r.top) / r.height) * 2 + 1,
    );
    desktopMouse.clientX = e.clientX;
    desktopMouse.clientY = e.clientY;
  };

  // Middle-button pans (via OrbitControls' pointer events); suppress the
  // browser's default middle-click auto-scroll cursor.
  dom.addEventListener("mousedown", (e) => {
    if (e.button === 1) e.preventDefault();
  });

  // Track right-button (orbit) / middle-button (pan) camera drags so hover
  // highlighting can switch off while the view is being moved. We only flip the
  // flag once the pointer clears a small dead-zone, so a bare right-click (e.g.
  // a context menu) doesn't blink the hover off. Button id, start point, and the
  // moved-yet flag are kept in this closure; the public state is `camDragging`.
  let _camBtn = -1; // 1 (middle) or 2 (right) while a hold is in progress, else -1
  let _camStartX = 0;
  let _camStartY = 0;
  const CAM_DRAG_DEADZONE = 4; // px before a hold counts as a drag
  dom.addEventListener("pointerdown", (e) => {
    if (e.button !== 1 && e.button !== 2) return;
    _camBtn = e.button;
    _camStartX = e.clientX;
    _camStartY = e.clientY;
  });
  window.addEventListener("pointermove", (e) => {
    if (_camBtn === -1 || desktopMouse.camDragging) return;
    if (Math.hypot(e.clientX - _camStartX, e.clientY - _camStartY) > CAM_DRAG_DEADZONE)
      desktopMouse.camDragging = true;
  });
  window.addEventListener("pointerup", (e) => {
    if (e.button !== _camBtn) return;
    _camBtn = -1;
    desktopMouse.camDragging = false;
  });

  dom.addEventListener("pointermove", (e) => {
    updateNDC(e);
    desktopMouse.overCanvas = true;
  });
  dom.addEventListener("pointerleave", () => {
    desktopMouse.overCanvas = false;
  });
  dom.addEventListener("pointerdown", (e) => {
    if (e.button !== 0) return; // left button only
    updateNDC(e);
    desktopMouse.overCanvas = true;
    desktopMouse.lmb = true;
    desktopMouse.pressShift = e.shiftKey;
    desktopMouse.pressSubtract = e.ctrlKey || e.metaKey;
    desktopMouse.pressAlt = e.altKey;
    _pressed = true;
  });
  // While the button is held, keep tracking the cursor on `window` so a drag
  // that wanders off the canvas (or over the toolbar) still updates the
  // position — the gesture follows the mouse instead of freezing at the edge.
  // `overCanvas` is intentionally NOT set here; the cursor really is off-canvas.
  window.addEventListener("pointermove", (e) => {
    if (desktopMouse.lmb) updateNDC(e);
  });
  // Listen for release on window so a drag that ends off-canvas still fires.
  window.addEventListener("pointerup", (e) => {
    if (e.button !== 0) return;
    if (desktopMouse.lmb) {
      desktopMouse.lmb = false;
      _released = true;
    }
  });
}

// A screen-space selection rectangle (the Select tool's marquee on desktop).
// Coordinates are CSS pixels. z-index sits under the toolbar but over canvas.
export function installDesktopMarqueeRect(): {
  show(x0: number, y0: number, x1: number, y1: number): void;
  hide(): void;
} {
  const el = document.createElement("div");
  el.style.cssText =
    "position:fixed;border:1px solid #6fa0ff;background:rgba(111,160,255,0.16);" +
    "pointer-events:none;z-index:9;display:none;";
  document.body.appendChild(el);
  return {
    show(x0, y0, x1, y1) {
      el.style.left = `${Math.min(x0, x1)}px`;
      el.style.top = `${Math.min(y0, y1)}px`;
      el.style.width = `${Math.abs(x1 - x0)}px`;
      el.style.height = `${Math.abs(y1 - y0)}px`;
      el.style.display = "block";
    },
    hide() {
      el.style.display = "none";
    },
  };
}
